import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import NotFound from "@/pages/NotFound";
import { GuideImg } from "@/components/guides/GuideBody";
import { useHostname } from "@/lib/ssr";
import { isGuidesEnabled } from "@/lib/guidesFlag";
import { isNewHomeEnabled } from "@/lib/homeFlag";
import { soraOnly, useSoraHeadings } from "@/lib/siteFont";
import { GUIDE_IMAGES } from "@/content/guides/images";
import { getMedia } from "@/content/media";
import EventTypesGrid from "@/components/EventTypesGrid";
import { EVENT_GROUPS, EVENT_INDEX_SEO, EVENT_TYPES_SECTION, EVENT_PAGES, EVENT_PHOTOGRAPHY_PATH, eventPagePath, getEventPage, type EventGroup } from "@/content/eventPhotography";
import { HUB_IMAGE } from "@/lib/seo/eventPhotography";
import { BlogArticle, Crumbs, type ArticleSection } from "./BlogPages";

// The event photography pages: /event-photography (the hub) and /event-photography/<slug>, server-rendered from
// src/content/eventPhotography/*.md with the blog's article layout. Live everywhere.

export const EVENT_SECTION: ArticleSection = { name: "Event photography", path: EVENT_PHOTOGRAPHY_PATH };

/** /event-photography/:slug */
export const EventPhotographyPage = () => {
  const { slug = "" } = useParams();
  const host = useHostname();
  const page = getEventPage(slug);
  if (!page) return <NotFound />;
  return <BlogArticle post={page} guidesOn={isGuidesEnabled(host)} newHome={isNewHomeEnabled(host)} section={EVENT_SECTION} />;
};

/** /event-photography */
export const EventPhotographyIndex = () => {
  const sora = useSoraHeadings();
  const hero = getMedia(HUB_IMAGE);
  return (
    <div className="min-h-screen bg-background">
      <AppHeader variant="marketing" />
      <main>
        <section className="relative overflow-hidden bg-hawk-navy pb-14 pt-32 text-white">
          <div className="absolute inset-0 bg-radial-orange opacity-20" aria-hidden="true" />
          <div className="container relative z-10 mx-auto max-w-4xl px-4">
            <Crumbs section={EVENT_SECTION} />
            <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-hawk-gold">Photohawk Studio</p>
            <h1 className={`mb-3 font-display text-4xl font-bold md:text-5xl${soraOnly(sora, "max-md:text-[2rem]")}`}>Event photography: by sport, and for organisers</h1>
            <p className="max-w-2xl text-lg text-white/80">{EVENT_INDEX_SEO.description}</p>
          </div>
        </section>
        <div className="container mx-auto max-w-4xl px-4 py-12">
          {hero && GUIDE_IMAGES[hero.name] && (
            <figure className="guide-figure mb-12 mt-0" data-testid="article-hero">
              <div className="overflow-hidden rounded-xl border border-border shadow-sm">
                <GuideImg img={GUIDE_IMAGES[hero.name]} alt={hero.alt} eager sizes="(min-width: 896px) 864px, 100vw" />
              </div>
              <figcaption>{hero.caption}</figcaption>
            </figure>
          )}
          {(Object.keys(EVENT_GROUPS) as EventGroup[]).map((g) => (
            <section key={g} aria-labelledby={`group-${g}`} className="mb-12">
              <h2 id={`group-${g}`} className="mb-2 font-display text-2xl font-bold">
                {EVENT_GROUPS[g].title}
              </h2>
              <p className="mb-5 text-muted-foreground">{EVENT_GROUPS[g].intro}</p>
              <ul className="m-0 grid list-none gap-4 p-0 sm:grid-cols-2" data-testid={`event-list-${g}`}>
                {EVENT_PAGES.filter((p) => p.group === g).map((p) => (
                  <li key={p.slug}>
                    <Link to={eventPagePath(p.slug)} className="group block h-full overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md">
                      {p.hero && GUIDE_IMAGES[p.hero.image] && (
                        <GuideImg img={GUIDE_IMAGES[p.hero.image]} alt="" sizes="(min-width: 640px) 420px, 100vw" className="block aspect-[16/9] h-auto w-full object-cover" />
                      )}
                      <span className="block p-5">
                        <span className="mb-1 block font-display text-lg font-bold leading-snug group-hover:text-hawk-orange">{p.title}</span>
                        <span className="block text-sm text-foreground/80">{p.description}</span>
                        <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(18_100%_38%)]">
                          Read more
                          <ArrowRight size={15} aria-hidden="true" />
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          <section aria-labelledby="group-more" className="mb-12" data-testid="event-types-section">
            <h2 id="group-more" className="mb-2 font-display text-2xl font-bold">
              {EVENT_TYPES_SECTION.title}
            </h2>
            <p className="mb-5 text-muted-foreground">{EVENT_TYPES_SECTION.intro}</p>
            <EventTypesGrid />
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};
